import { useSignIn } from '@clerk/clerk-react';
import { isClerkAPIResponseError } from '@clerk/clerk-react/errors';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { OAuthButton } from '@/components/common/auth/OAuthButton';
import { OtpInput } from '@/components/common/auth/OtpInput';
import { FormInput } from '@/components/common/form-inputs/FormInput';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OAUTH_PROVIDER_ORDER } from '@/constants/auth-providers';
import {
  getLastAuthMethod,
  rememberAuthMethod,
} from '@/helpers/auth-method.helper';
import { orgPath, orgUrl } from '@/helpers/org-path.helper';
import { useOAuthSignIn } from '@/hooks/auth/useOAuthSignIn';
import { toast } from '@/hooks/ui/use-toast';
import logger from '@/services/logger.service';

/**
 * Passwordless-first sign in.
 *
 * The email screen never says whether an account exists - it always moves to
 * "check your email" - so the form cannot be used to enumerate accounts.
 * Password stays available as its own button for everyone who already has
 * one, and for the case where the email never arrives.
 */

const identifierSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
});

const passwordSchema = z.object({
  password: z.string().min(1, { message: 'Password is required' }),
});

type IdentifierFormValues = z.infer<typeof identifierSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

type Step = 'identifier' | 'code' | 'password';

const CODE_LENGTH = 6;
const GENERIC_ERROR = 'Something went wrong. Please try again.';

export const SignInForm = () => {
  // state
  const [step, setStep] = useState<Step>('identifier');
  const [email, setEmail] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [codeError, setCodeError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  // custom hooks
  const { isLoaded, signIn, setActive } = useSignIn();
  const { pendingProvider, authenticateWith } = useOAuthSignIn();

  const identifierForm = useForm<IdentifierFormValues>({
    resolver: zodResolver(identifierSchema),
    defaultValues: { email: '' },
  });

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '' },
  });

  // derived values
  const lastAuthMethod = getLastAuthMethod();

  // event handlers
  const completeSignIn = async (
    createdSessionId: string | null,
    method: 'password' | 'email_code'
  ) => {
    rememberAuthMethod(method);
    await setActive?.({
      session: createdSessionId,
      redirectUrl: orgUrl('/dashboard'),
    });
  };

  /** Asks Clerk to email a code. Silently no-ops when the account is unknown. */
  const sendCode = async (address: string) => {
    if (!isLoaded || !signIn) return;

    try {
      const attempt = await signIn.create({ identifier: address });
      const factor = attempt.supportedFirstFactors?.find(
        (candidate) => candidate.strategy === 'email_code'
      );

      if (!factor || !('emailAddressId' in factor)) {
        // The instance has not enabled email codes - fall back to the password
        // screen so the form still works.
        logger.error('Email code factor unavailable', undefined, {
          factors: attempt.supportedFirstFactors?.map((f) => f.strategy),
        });
        setStep('password');
        return;
      }

      await signIn.prepareFirstFactor({
        strategy: 'email_code',
        emailAddressId: factor.emailAddressId,
      });
    } catch (error) {
      // An unknown identifier must look exactly like a known one from here.
      if (
        isClerkAPIResponseError(error) &&
        error.errors[0]?.code === 'form_identifier_not_found'
      ) {
        return;
      }
      throw error;
    }
  };

  const handleIdentifierSubmit = async (values: IdentifierFormValues) => {
    if (!isLoaded || !signIn) {
      toast({
        variant: 'destructive',
        title: 'Sign in is not ready yet',
        description: 'Give it a moment and try again',
      });
      return;
    }

    setLoading(true);
    setEmail(values.email);
    setCode('');
    setCodeError('');

    try {
      await sendCode(values.email);
      setStep((current) => (current === 'password' ? current : 'code'));
    } catch (error) {
      logger.error('Could not start email sign-in', error as Error, {
        email: values.email,
      });
      toast({
        variant: 'destructive',
        title: 'Could not send the code',
        description: GENERIC_ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCodeComplete = async (submitted: string) => {
    if (!isLoaded || !signIn) return;

    setLoading(true);
    setCodeError('');
    try {
      const attempt = await signIn.attemptFirstFactor({
        strategy: 'email_code',
        code: submitted,
      });

      if (attempt.status === 'complete') {
        await completeSignIn(attempt.createdSessionId, 'email_code');
        return;
      }

      setCodeError('That code did not work. Request a new one.');
    } catch (error) {
      if (isClerkAPIResponseError(error)) {
        const clerkCode = error.errors[0]?.code;
        setCodeError(
          clerkCode === 'form_code_incorrect' ||
            clerkCode === 'verification_expired'
            ? 'That code is wrong or expired. Request a new one.'
            : error.errors[0]?.longMessage || GENERIC_ERROR
        );
        setLoading(false);
        return;
      }

      logger.error('Email code verification failed', error as Error, { email });
      setCodeError(GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  };

  /** Escape hatch behind "Show other options" - no code is sent. */
  const handleUsePassword = identifierForm.handleSubmit((values) => {
    setEmail(values.email);
    setStep('password');
  });

  const handlePasswordSubmit = async (values: PasswordFormValues) => {
    if (!isLoaded || !signIn) return;

    setLoading(true);
    try {
      const attempt = await signIn.attemptFirstFactor({
        strategy: 'password',
        password: values.password,
      });

      if (attempt.status === 'complete') {
        await completeSignIn(attempt.createdSessionId, 'password');
        return;
      }

      logger.error('Sign-in did not complete', undefined, {
        status: attempt.status,
      });
      toast({
        variant: 'destructive',
        title: 'Sign in incomplete',
        description: 'This account needs another step. Contact support.',
      });
    } catch (error) {
      if (isClerkAPIResponseError(error)) {
        const clerkCode = error.errors[0]?.code;

        // No password on this account - the emailed code is the way in.
        if (clerkCode === 'strategy_for_user_invalid') {
          passwordForm.setError('password', {
            message: 'This account has no password. Use the emailed code.',
          });
          setLoading(false);
          return;
        }

        if (clerkCode === 'form_password_incorrect') {
          passwordForm.setError('password', {
            message: 'Incorrect password.',
          });
          setLoading(false);
          return;
        }
      }

      logger.error('Password sign-in failed', error as Error, { email });
      toast({
        variant: 'destructive',
        title: 'Sign in failed',
        description: isClerkAPIResponseError(error)
          ? error.errors[0]?.longMessage || GENERIC_ERROR
          : GENERIC_ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setStep('identifier');
    setCode('');
    setCodeError('');
    passwordForm.reset();
    identifierForm.setValue('email', email);
  };

  const handleBackToCode = () => setStep('code');

  // effects
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const devEmail = import.meta.env.VITE_DEV_AUTO_LOGIN_EMAIL;
    const devPassword = import.meta.env.VITE_DEV_AUTO_LOGIN_PASSWORD;
    if (!isLoaded || !signIn || !devEmail || !devPassword) return;

    setLoading(true);
    signIn
      .create({ identifier: devEmail, password: devPassword })
      .then(async (attempt) => {
        if (attempt.status === 'complete') {
          await setActive?.({
            session: attempt.createdSessionId,
            redirectUrl: orgUrl('/dashboard'),
          });
        }
      })
      .catch(() => setLoading(false));
  }, [isLoaded]);

  if (step === 'code') {
    return (
      <div className="w-full space-y-5">
        <div className="space-y-2 text-center">
          <h2 className="as-strong text-black">Check your email</h2>
          <p className="as-body-03 text-muted-foreground">
            If you have an account, we sent a code to{' '}
            <span className="as-strong text-black">{email}</span>.
          </p>
        </div>

        <OtpInput
          value={code}
          length={CODE_LENGTH}
          disabled={loading}
          onChange={setCode}
          onComplete={handleCodeComplete}
        />

        {codeError && (
          <p className="as-body-03 text-center text-destructive">{codeError}</p>
        )}

        {loading && (
          <div className="flex justify-center">
            <Loader2 className="size-4 animate-spin" />
          </div>
        )}

        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleUseDifferentEmail}
            className="as-strong text-black hover:underline"
          >
            Use a Different Email
          </button>
        </div>
      </div>
    );
  }

  if (step === 'password') {
    return (
      <div className="w-full space-y-4">
        <h2 className="as-strong mb-6 text-black">Login to your account</h2>

        {/* A real, visible field so password managers still match this account. */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="signed-in-email">Email</Label>
            <button
              type="button"
              onClick={handleUseDifferentEmail}
              className="as-body-03 text-muted-foreground underline hover:text-black"
            >
              Change
            </button>
          </div>
          <Input
            id="signed-in-email"
            name="email"
            type="email"
            value={email}
            readOnly
            autoComplete="username"
          />
        </div>

        <Form {...passwordForm}>
          <form
            onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)}
            className="w-full space-y-4"
          >
            <div className="relative">
              <FormInput
                control={passwordForm.control}
                name="password"
                type="password"
                label="Password"
                placeholder="Password"
                autoComplete="current-password"
              />
              <Link
                to={orgPath('/auth/forget-password')}
                className="as-body-03 absolute right-0 top-0 text-muted-foreground underline hover:text-black"
              >
                Forgot your password?
              </Link>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </Form>

        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleBackToCode}
            className="as-body-03 text-muted-foreground underline hover:text-black"
          >
            Email me a code instead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <h2 className="as-strong mb-6 text-black">Login to your account</h2>

      <Form {...identifierForm}>
        <form
          onSubmit={identifierForm.handleSubmit(handleIdentifierSubmit)}
          className="w-full space-y-3"
        >
          <FormInput
            control={identifierForm.control}
            name="email"
            type="email"
            label="Email"
            placeholder="Email address"
            autoComplete="username"
          />
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              'Continue with Email'
            )}
          </Button>
        </form>
      </Form>

      <div className="h-[1px] w-full bg-neutral-200" />

      <div className="space-y-3">
        {OAUTH_PROVIDER_ORDER.map((provider) => (
          <OAuthButton
            key={provider}
            provider={provider}
            loading={pendingProvider === provider}
            disabled={Boolean(pendingProvider) && pendingProvider !== provider}
            showLastUsed={lastAuthMethod === provider}
            onClick={authenticateWith}
          />
        ))}

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleUsePassword}
        >
          <KeyRound className="size-4" />
          Continue with Password
        </Button>
      </div>
    </div>
  );
};
